import { useState, useRef, useEffect, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { floatingMenuPosition, type FloatingMenuPosition } from '@/shared/utils/floatingMenuPosition';
import { useSelectKeyboard } from './useSelectKeyboard';
import { INPUT_BASE, INPUT_STATE } from './input/inputStyles';

interface Option {
    value: string;
    label: string;
}

interface SelectProps {
    id?: string;
    options: Option[];
    placeholder?: string;
    /**
     * The selected option's value. `Select` is a **strictly controlled** component: the label it
     * displays is derived from `value` during render, and it holds no selection state of its own.
     * A consumer must therefore write the value it receives from `onChange` back into `value`, or
     * the selection will not appear. Every call-site in `www/src` does (37 via a react-hook-form
     * `Controller`, 20 via component or hook state) — see
     * docs/plans/2026-08-04-select-derived-state-removal.md.
     *
     * A `value` that is absent from `options` renders the placeholder, immediately and by design:
     * PlanVehicleRulePanel.tsx:102-106 relies on it so a stale UUID cannot hide behind the
     * placeholder.
     */
    value?: string;
    /**
     * Fired when the user picks an option, and once when `options` narrows to exactly one entry
     * that is not already selected (see the auto-select effect).
     */
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    error?: boolean;
    hint?: string;
    className?: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    isLoading?: boolean; // Add loading prop
    // Links the combobox to persistent helper text (e.g. a disabled reason). Merged with
    // the rendered `hint`'s own id into the native `aria-describedby`.
    ariaDescribedBy?: string;
    // Accessible name for a Select with no visible <Label>. Defaults to `placeholder`,
    // which names the control after its empty state — pass the field name instead.
    ariaLabel?: string;
    // Id of a visible <Label> naming the combobox; a native htmlFor cannot target a div.
    ariaLabelledBy?: string;
}

const MENU_MAX_HEIGHT = 192;

export const Select: React.FC<SelectProps> = ({
    id,
    options,
    placeholder = 'Select an option',
    value,
    onChange,
    onBlur,
    disabled = false,
    error = false,
    hint,
    className = '',
    size = 'md',
    isLoading = false,
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledBy,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const fallbackId = useId();
    const hintId = `${id ?? fallbackId}-hint`;
    const describedBy =
        [ariaDescribedBy, hint ? hintId : undefined].filter(Boolean).join(' ') || undefined;
    // Derived during render, never mirrored into state: a passive effect cannot run in the
    // mounting commit, so mirroring made every pre-populated Select paint its placeholder for
    // one frame before its label (#759).
    const selectedOption = options.find((opt) => opt.value === value) ?? null;
    // Portal menu position (fixed/viewport coords) so the dropdown escapes any
    // overflow-clipped ancestor such as the shared Table's overflow-x-auto wrapper.
    const [menuPos, setMenuPos] = useState<FloatingMenuPosition | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Announce a sole option to the parent. The label is derived from `value`, so this only
    // announces — the parent must accept the value for the option to display. `announcedRef`
    // records what has been announced for the current single-option set so a parent that does not
    // accept it cannot drive an unbounded onChange/render loop; it re-arms whenever the parent
    // accepts or the single-option condition lapses.
    const announcedRef = useRef<string | null>(null);
    useEffect(() => {
        if (isLoading || options.length !== 1) {
            announcedRef.current = null;
            return;
        }
        const [only] = options;
        if (value === only.value) {
            announcedRef.current = null;
            return;
        }
        // `onChange` is optional, so bail before the record is touched: latching a value that was
        // never emitted would make the effect's own re-run — `onChange` is a dependency — return
        // at the guard below, and a handler attached after mount would never hear the auto-select.
        if (!onChange) return;
        if (announcedRef.current === only.value) return;
        announcedRef.current = only.value;
        onChange(only.value);
    }, [options, value, isLoading, onChange]);

    useLayoutEffect(() => {
        if (!isOpen || !triggerRef.current) return;
        setMenuPos(floatingMenuPosition(triggerRef.current.getBoundingClientRect(), window.innerHeight, MENU_MAX_HEIGHT));
    }, [isOpen]);

    // Close when clicking outside both the trigger and the portalled menu.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const inTrigger = triggerRef.current?.contains(target);
            const inMenu = menuRef.current?.contains(target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // A fixed-position menu can't follow scroll/resize, so close it to avoid drift.
    // The scroll listener is capture-phase (scroll events don't bubble), so it also sees
    // scrolls inside the portalled menu's own option list — ignore those, or scrolling to
    // options below the max-height fold would tear the menu down before they can be clicked.
    useEffect(() => {
        if (!isOpen) return;
        const handleScroll = (event: Event) => {
            if (menuRef.current?.contains(event.target as Node)) return;
            setIsOpen(false);
        };
        const handleResize = () => setIsOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen]);

    const handleOptionClick = (option: Option) => {
        setIsOpen(false);
        onChange?.(option.value);
    };

    const optionIdBase = `${id ?? fallbackId}-option`;
    const optionId = (index: number) => `${optionIdBase}-${index}`;
    const { activeIndex, setActiveIndex, toggleMenu, handleKeyDown } = useSelectKeyboard({
        isOpen,
        setIsOpen,
        options,
        selectedIndex: selectedOption ? options.indexOf(selectedOption) : -1,
        canInteract: !disabled && !isLoading,
        onSelect: handleOptionClick,
        triggerRef,
        optionId,
        isMenuMounted: isOpen && !isLoading && menuPos !== null,
    });
    const activeDescendant = isOpen && !isLoading && activeIndex >= 0 ? optionId(activeIndex) : undefined;

    const sizeClasses = {
        xs: 'px-3 py-1 h-8 text-xs',
        sm: 'px-3 py-2 h-9 text-sm',
        md: 'px-4 py-2.5 h-11 text-sm',
        lg: 'px-4 py-3 h-12 text-base',
    };

    const stateClasses =
        disabled || isLoading
            ? INPUT_STATE.disabled
            : `cursor-pointer ${error ? INPUT_STATE.error : INPUT_STATE.default}`;
    const selectClasses = twMerge(
        INPUT_BASE,
        'relative flex justify-between items-center transition-colors',
        sizeClasses[size],
        stateClasses,
        className
    );

    return (
        <div className="relative">
            <div
                ref={triggerRef}
                id={id}
                className={selectClasses}
                onClick={toggleMenu}
                onKeyDown={handleKeyDown}
                onBlur={onBlur}
                tabIndex={disabled || isLoading ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen ? 'true' : 'false'}
                aria-haspopup="listbox"
                aria-controls={`${id || 'select'}-listbox`}
                aria-activedescendant={activeDescendant}
                aria-labelledby={ariaLabelledBy}
                aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? placeholder)}
                aria-disabled={disabled || isLoading ? 'true' : undefined}
                aria-describedby={describedBy}
                aria-invalid={error ? true : undefined}
            >
                <span
                    className={`overflow-hidden text-ellipsis text-nowrap w-full mr-5 ${selectedOption ? 'text-mp-foreground' : 'text-mp-muted'}`}
                >
                    {isLoading ? (
                        <span className="flex items-center">
                            <svg
                                className="animate-spin mr-2 h-4 w-4 text-mp-muted"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                            Loading...
                        </span>
                    ) : (
                        selectedOption?.label || placeholder
                    )}
                </span>

                {/* Dropdown Arrow */}
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                        className={`h-5 w-5 text-mp-muted transition-transform duration-200 ${
                            isOpen ? 'rotate-180' : ''
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            </div>

            {/* Dropdown Options — portalled to the body so it is never clipped by an
                overflow-hidden/scroll ancestor (e.g. a data table). Because the portal escapes
                the DOM (and stacking context) of any ancestor, the z-index must clear the shared
                Modal overlay (z-99999, see ui/modal/Modal.tsx) so a Select opened inside a modal
                is not painted behind it; kept below the toast layer (999999, see AppLayout.tsx). */}
            {isOpen &&
                !isLoading &&
                menuPos &&
                createPortal(
                    <div
                        ref={menuRef}
                        style={{
                            position: 'fixed',
                            top: menuPos.top,
                            bottom: menuPos.bottom,
                            left: menuPos.left,
                            width: menuPos.width,
                            zIndex: 100000,
                        }}
                        className="bg-mp-surface border border-mp-input rounded-lg shadow-lg"
                        onMouseDown={(event) => event.preventDefault()}
                    >
                        <div
                            className="overflow-y-auto"
                            style={{ maxHeight: menuPos.maxHeight }}
                            role="listbox"
                            id={`${id || 'select'}-listbox`}
                        >
                            {options.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-mp-muted">
                                    No options available
                                </div>
                            ) : (
                                options.map((option, index) => (
                                    <button
                                        type="button"
                                        key={option.value}
                                        id={optionId(index)}
                                        tabIndex={-1}
                                        className={`w-full text-left px-3 py-2 text-sm text-mp-foreground focus:outline-none transition-colors ${
                                            selectedOption?.value === option.value
                                                ? 'bg-mp-primary-content'
                                                : index === activeIndex
                                                  ? 'bg-mp-primary/10'
                                                  : ''
                                        }`}
                                        onClick={() => handleOptionClick(option)}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        role="option"
                                        aria-selected={
                                            selectedOption?.value === option.value
                                                ? 'true'
                                                : 'false'
                                        }
                                    >
                                        {option.label}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>,
                    document.body
                )}

            {/* Error/Hint Message */}
            {hint && (
                <p
                    id={hintId}
                    className={`mt-1 text-xs ${error ? 'text-mp-error' : 'text-mp-muted'}`}
                >
                    {hint}
                </p>
            )}
        </div>
    );
};
